import { useEffect } from "react"
import { Link, useLocation, useParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from "react-bootstrap/esm/Button.js"
import DropdownButton from 'react-bootstrap/DropdownButton'
import 'react-datepicker/dist/react-datepicker.css'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import { InputGroup } from "react-bootstrap"
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ApplyConfigTemplateModal from "./components/ApplyConfigTemplateModal.jsx"
import ConfirmationApplyProfileModal from "./components/ConfirmationApplyProfileModal.jsx"
import ConfirmationModal from "../../components/ConfirmationModal"
import ConfirmationRemoveUtgModal from './components/ConfirmationRemoveUtgModal'
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle.jsx"
import ScheduleTaskModal from "./components/ScheduleTaskModal"
import UtgsInfoModal from './components/UtgsInfoModal.jsx'
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"
import UtgListFilteringModal from "./components/UtgListFilteringModal.jsx"

import { ApplyConfigTemplate, ApplyProfile, DeleteUtg, ExportUtgList } from '../../utils/api.js'

import { NotificationTypes, addNotification } from "../../stores/notification-store.js"

import useAgentManifest from "../../hooks/useAgentManifest.js"
import useConfigTemplates from "../../hooks/useConfigTemplates.js"
import useProfiles from "../../hooks/useProfiles.js"
import useUtgManifest from "../../hooks/useUtgManifest.js"

import { utgListSignal, utgListLoadingSignal as utgListLoadingSignal } from "../../state/signals/utgSignals.js"
import { fetchUtgList } from "../../state/actions/utgActions.js"
import { pageSizeSignal as rowsPerPageSignal, debugSignal, utgListFiltersValueSignal as filtersValueSignal, utgListFiltersCountSignal, selectedBusinessEntityIdSignal, utgListFiltersValueSignal } from "../../state/signals/userSettingsSignals.js"
import { removeUtgListFilter, updatePageSize, updateUtgListFilter } from "../../state/actions/userSettingsActions.js"

import { SortDirectionType } from '../../utils/constants'
import { computeSortedData, saveFile } from '../../utils/utils'

function UtgList() {

    const navigate = useNavigate()
    const { beId } = useParams()
    const { config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])
    
    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditUtgs
    })

    const showUtgsInfoModalSignal = useSignal(false)

    const location = useLocation()
    let intent
    if (location && location.state) {
        intent = location.state.intent
        location.state.intent = null
    } else {
        intent = null
    }

    const { agents: availableAgentsSignal } = useAgentManifest(config, accessToken)
    const { utgs: availableutgListSignal } = useUtgManifest(config, accessToken)
    const { configTemplates: configTemplatesSignal } = useConfigTemplates(beId, config, accessToken)
    const { profiles: availableProfilesSignal, loading: profilesLoadingSignal, error: profilesErrorSignal } = useProfiles(beId, config, accessToken)

    const currentPageSignal = useSignal(1)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')
    const isSelectAllSignal = useSignal(false)
    const selectedRowsSignal = useSignal([])
    const selectedRowsCountLabelSignal = useComputed(() => {
        return `(${selectedRowsSignal.value.length})`
    })

    useEffect(() => {
        if (selectedBusinessEntityIdSignal.value !== beId) {
            removeUtgListFilter()
        }
        fetchUtgList(beId, filtersValueSignal.value, config, accessToken)
    }, [beId])

    const sortDirectionSignal = useSignal(SortDirectionType.ASCENDING)

    const filteredDataSignal = useComputed(() => {
        if (!filterStringSignal.value) {
            return utgListSignal.value ? [...utgListSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return utgListSignal.value.filter(item => {
            // Limit search to these fields
            let searchData = {
                ComponentMerchant: item.componentMerchant,
                ComponentNickname: item.componentNickname,
                UtgStatus: item.utgStatus,
                UtgVersion: item.utgVersion,
                AgentVersion: item.agentVersion,
                ConfigStatus: item.configStatus,
                ComponentProfileName: item.componentProfileName,
                ComponentProfileVersion: item.componentProfileVersion,
                ComponentLocation: item.componentLocation,
                ComponentLane: item.componentLane,
                ComponentFileId: item.componentFileId,
                MachineDnsName: item.machineDnsName,
                MachineStatus: item.machineStatus,
                MachineStatusEntityLastKnownCommunication: item.machineStatusEntityLastKnownCommunication,
                MachineStatusEntityNextPredictedCommunication: item.machineStatusEntityNextPredictedCommunication,
                MachineNickname: item.machineNickname,
                BusinessEntityId: item.businessEntityId,
                StandardTimeZone: item.machineStatusEntityStandardTimeZone,
                WindowsVersion: item.machineStatusEntityWindowsVersion,

                ComponentPermanentId: item.componentPermanentId,
                MachinePermanentId: item.machinePermanentId,
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedDataSignal = useComputed(computeSortedData(filteredDataSignal, sortColumnSignal, sortDirectionSignal))

    const sortedProfilesSignal = useComputed(computeSortedData(availableProfilesSignal, "name"))

    const totalPagesSignal = useComputed(() => {
        return Math.ceil(filteredDataSignal.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayDataSignal = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedDataSignal.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumberSignal = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumberSignal = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredDataSignal.value.length ? endIndex : filteredDataSignal.value.length
    })

    const gaUtgSignal = useComputed(() => {
        if (availableutgListSignal.value.length) {
            const filteredUtgs = availableutgListSignal.value.filter((a) => a.generalAvailability === true
            )
            return filteredUtgs[0].version
        }
    })

    const gaAgentSignal = useComputed(() => {
        if (availableAgentsSignal.value.length) {
            const filteredAgents = availableAgentsSignal.value.filter((a) => a.generalAvailability === true
            )
            return filteredAgents[0].version
        }
    })

    //Export UTGs as csv
    const showDownloadProgressSignal = useSignal(false)
    const downloadProgressMessageSignal = useSignal('')
    const showExportConfirmationModalSignal = useSignal(false)

    const handleUtgListExport = async () => {
        // set showDownloadProgress to true to show spinner
        showDownloadProgressSignal.value = true
        downloadProgressMessageSignal.value = 'Preparing to export UTG list'
        try {
            const exportUtgListResponse = await ExportUtgList(beId, config, accessToken)
            saveFile(exportUtgListResponse, "components.csv")
            addNotification('Exporting UTG list. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
            showDownloadProgressSignal.value = false
        }
        catch (error) {
            console.log(error)
            addNotification(`Unable to UTG list. ${error}`, NotificationTypes.ERROR, true)
            showDownloadProgressSignal.value = false
        }
    }

    // Task modal
    const showModalTaskSignal = useSignal(false)
    const handleCancelModalTask = async () => {
        showModalTaskSignal.value = false
    }

    // Config Template modal
    const selectedConfigTemplateIdSignal = useSignal(-1)
    const showModalConfigTemplateSignal = useSignal(false)
    const handleCancelModalConfigTemplate = () => showModalConfigTemplateSignal.value = false
    const handleSubmitModalConfigTemplate = () => {
        showModalConfigTemplateSignal.value = false

        let configTemplatePromises = []
        for (let i = 0; i < selectedRowsSignal.value.length; i++) {
            try {
                configTemplatePromises.push(ApplyConfigTemplate(beId, selectedConfigTemplateIdSignal.value, selectedRowsSignal.value[i].componentId, config, accessToken))
            } catch (error) {
                addNotification("Exception when pushing apply config template promise: ".concat(error.toString()), NotificationTypes.ERROR, false)
            }
        }
        Promise.all(configTemplatePromises)
            .then(results => {
                addNotification('The config template was merged into the UTGs successfully', NotificationTypes.SUCCESS, false)
            })
            .catch(error => {
                addNotification('Unable to merge config template with all UTGs. Caught exception: '.concat(error.toString()), NotificationTypes.ERROR, false)
            })
    }
    const handleApplyConfigTemplate = (item) => {
        if (item) {
            // if array does not contain item, add item
            if (!selectedRowsSignal.value.includes(item)) {
                selectedRowsSignal.value = [...selectedRowsSignal.value, item]
            }
            showModalConfigTemplateSignal.value = true
        }
    }

    // UTG Filtering Modal
    const showUtgFilteringModalSignal = useSignal(false)
    const handleCancelUtgFiltering = () => showUtgFilteringModalSignal.value = false
    const handleApplyUtgFilters = (predicates) => {
        showUtgFilteringModalSignal.value = false
        updateUtgListFilter(predicates)
        fetchUtgList(beId, predicates, config, accessToken)
    }
    const handleClearFilter = () => {
        removeUtgListFilter()
        fetchUtgList(beId, utgListFiltersValueSignal.value, config, accessToken)
    }


    const accordionData = [
        { name: 'Properties', data: { Count: utgListSignal.length, 'Filter Count': filteredDataSignal.value.length, 'Display Count': displayDataSignal.value.length, Filter: filterStringSignal.value, CurrentPage: currentPageSignal.value, RowsPerPage: rowsPerPageSignal.value, accessToken: accessToken } },
        { name: 'Filtered Data', data: filteredDataSignal.value },
        { name: 'Data', data: utgListSignal.value },
        { name: 'Display Data', data: displayDataSignal.value },
        { name: 'Selected Rows', data: selectedRowsSignal.value },
        { name: 'Available Agents', data: availableAgentsSignal.value },
        { name: 'Available Utgs', data: availableutgListSignal.value },
        { name: 'Available Profiles', data: availableProfilesSignal },
        { name: 'Config Templates', data: configTemplatesSignal.value },
        { name: 'Selected Config Template', data: selectedConfigTemplateIdSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        ...sidebarAccordionData
    ]

    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        updatePageSize(parseInt(v, 10))
    }
    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }

    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.componentNickname} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }
    const handleScheduleTask = item => {
        if (item) {
            console.log('handleScheduleTask', item)
            // if array does not contain item, add item
            if (!selectedRowsSignal.value.includes(item)) {
                selectedRowsSignal.value = [...selectedRowsSignal.value, item]
            }
            showModalTaskSignal.value = true
        }
    }


    // Remove UTGs
    const showRemoveUtgModalSignal = useSignal(false)
    const handleCancelRemoveUtg = () => showRemoveUtgModalSignal.value = false
    const handleSubmitModalRemoveUtg = () => {
        showRemoveUtgModalSignal.value = false

        let promises = []
        for (let i = 0; i < selectedRowsSignal.value.length; i++) {
            try {
                promises.push(DeleteUtg(beId, selectedRowsSignal.value[i].machinePermanentId, selectedRowsSignal.value[i].componentPermanentId, config, accessToken))
            } catch (error) {
                addNotification(`Unable to remove all selected UTGs`, NotificationTypes.ERROR, false, 'Remove UTGs Failed')
            }
        }
        Promise.all(promises)
            .then(async (results) => {
                addNotification('The selected UTGs were successfully removed.', NotificationTypes.SUCCESS, false)
                await fetchUtgList(beId, null, config, accessToken)
            })
            .catch(error => {
                addNotification(`Unable to remove all selected UTGs`, NotificationTypes.ERROR, false, 'Remove UTGs Failed')
            })
    }

    const handleRemoveUtg = (item) => {
        if (item) {
            // if array does not contain item, add item
            if (!selectedRowsSignal.value.includes(item)) {
                selectedRowsSignal.value = [...selectedRowsSignal.value, item]
            }
            showRemoveUtgModalSignal.value = true
        }
    }

    // Apply Profiles    
    const showApplyProfileModalSignal = useSignal(false)
    const selectedProfileSignal = useSignal(-1)
    const handleCancelApplyProfile = () => showApplyProfileModalSignal.value = false
    const handleSubmitModalApplyProfile = async () => {
        showApplyProfileModalSignal.value = false
        try {
            const utgIds = selectedRowsSignal.value.map(utg => utg.componentId)
            await ApplyProfile(beId, selectedProfileSignal.value, utgIds, config, accessToken)
            addNotification('The profile was applied to the selected UTGs.', NotificationTypes.SUCCESS, false)
        } catch (error) {
            addNotification(`Unable to apply the profile to the selected UTGs`, NotificationTypes.ERROR, false, 'Apply Profile Failed')
        }
    }

    const handleApplyProfile = (item) => {
        if (item) {
            // if array does not contain item, add item
            if (!selectedRowsSignal.value.includes(item)) {
                selectedRowsSignal.value = [...selectedRowsSignal.value, item]
            }
            showApplyProfileModalSignal.value = true
        }
    }


    const handleSelectAll = () => {

        if (isSelectAllSignal.value) {
            // deselect all
            selectedRowsSignal.value = []
            isSelectAllSignal.value = false
            return
        }

        // select all rows from displayed data. Considered using filteredData but support prefers to limit to current view only
        isSelectAllSignal.value = true
        selectedRowsSignal.value = displayDataSignal.value.map(item => {
            return item
        })
    }

    const handleSelectRow = (item) => {
        // Check if the item is already selected
        const wasItemSelected = selectedRowsSignal.value.some(v => v.componentId === item.componentId);

        if (wasItemSelected) {
            // User wants to deselect, so filter out the item
            selectedRowsSignal.value = selectedRowsSignal.value.filter(v => v.componentId !== item.componentId);
        } else {
            // Add the item to the selectedRows array
            selectedRowsSignal.value = [...selectedRowsSignal.value, item];
        }
    };


    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    function handleCancelModalUtgsInfo() {
        showUtgsInfoModalSignal.value = false
    }

    function TableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.componentId}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="19" className="text-center">No UTGs found</td></tr></tbody>
            )
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td><Form.Check aria-label="Select Row" checked={selectedRowsSignal.value.some(row => row.componentId === item.componentId)} onChange={() => handleSelectRow(item)} /></td>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleScheduleTask(item)}>Schedule Task</Dropdown.Item>}
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleApplyProfile(item)}>Apply a Profile</Dropdown.Item>}
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleApplyConfigTemplate(item)}>Apply a Config Template</Dropdown.Item>}
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleRemoveUtg(item)}>Remove From Command Center</Dropdown.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.componentMerchant}</td>
                <td><Link to={`../../be/${item.businessEntityId}/utgs/${item.componentId}`}>{item.componentNickname}</Link></td>
                <td>{item.utgStatus}</td>
                <td>{item.utgVersion}</td>
                <td>{item.agentVersion}</td>
                <td>{item.configStatus}</td>
                <td>{item.componentProfileName}</td>
                <td>{item.componentProfileVersion}</td>
                <td>{item.componentLocation}</td>
                <td>{item.componentLane}</td>
                <td>{item.componentFileId}</td>
                <td>{item.machineDnsName}</td>
                <td>{item.machineStatus}</td>
                <td><TimestampWithUTCTooltip date={item.machineStatusEntityLastKnownCommunication}></TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip date={item.machineStatusEntityNextPredictedCommunication}></TimestampWithUTCTooltip></td>
                <td>{item.machineNickname}</td>
                <td>{item.businessEntityId}</td>
                <td>{item.machineStatusEntityStandardTimeZone}</td>
                <td>{item.machineStatusEntityWindowsVersion}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPagesSignal.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    function DownloadProgress() {
        if (showDownloadProgressSignal.value) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <Spinner className="mt-5" animation="border" role="status">
                        <span className="visually-hidden">{downloadProgressMessageSignal.value}</span>
                    </Spinner>
                    <p className="mt-3">{downloadProgressMessageSignal.value}</p>
                </div>
            )
        }
    }

    useEffect(() => {
        if (!utgListLoadingSignal.value && utgListSignal.value && utgListSignal.value.length && !profilesLoadingSignal.value && intent == "schedule") {
            intent = null
            addNotification(`Schedule a Task by clicking on the gear icon in the table below.`, NotificationTypes.INFO, false, 'Select a UTG')
        }
    })

    if (utgListLoadingSignal.value || profilesLoadingSignal.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
            <DownloadProgress></DownloadProgress>
            <ConfirmationModal
                show={showExportConfirmationModalSignal.value}
                handleCancel={() => showExportConfirmationModalSignal.value = false}
                handleConfirm={() => { showExportConfirmationModalSignal.value = false; handleUtgListExport(); }}
                message={'Export may take a few minutes to download ALL components on this tab. Are you sure you want to continue?'}
                title={"Export UTG List"}
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item active>UTG List</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='UTG List' canEditSignal={canEditSignal} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Stack direction="horizontal" gap={3} className="me-auto">
                    <Nav.Item >
                        <InputGroup>
                            <Button size="md" variant="outline-secondary" onClick={() => handleClearFilter()}>
                                <Icon icon='trash' width="16" height="16"></Icon>
                            </Button>
                            <Button size="md" variant="outline-secondary" onClick={() => showUtgFilteringModalSignal.value = true}>
                                <Icon icon='funnel' width="16" height="16"></Icon>
                                &nbsp;Filters ({utgListFiltersCountSignal.value})
                            </Button>
                        </InputGroup>
                    </Nav.Item>
                    <DropdownButton variant="outline-secondary" title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
                        <Dropdown.Item eventKey="10">10</Dropdown.Item>
                        <Dropdown.Item eventKey="25">25</Dropdown.Item>
                        <Dropdown.Item eventKey="50">50</Dropdown.Item>
                        <Dropdown.Item eventKey="250">250</Dropdown.Item>
                        <Dropdown.Item eventKey="500">500</Dropdown.Item>
                        <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                    </DropdownButton>
                </Stack>
                <Nav.Item className="mt-1 me-2">
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" checked={isSelectAllSignal.value} onChange={handleFilterStringChange} />
                </Nav.Item>
                <Nav.Item>
                    <Button variant="outline-secondary" onClick={() => { showUtgsInfoModalSignal.value = true }}>
                        <Icon icon="info-circle" height={16} width={16}></Icon>
                    </Button>
                    &nbsp;
                </Nav.Item>
                <Nav.Item className="mt-1">
                    <Button size="sm" variant="outline-secondary" onClick={() => showExportConfirmationModalSignal.value = true}>
                        <Icon icon='download' width="16" height="16"></Icon>
                        &nbsp;Download data as CSV
                    </Button>
                </Nav.Item>
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <td><Form.Check type="checkbox" id="select-all-switch" aria-label="Select All" label={selectedRowsCountLabelSignal.value} checked={isSelectAllSignal.value} onChange={() => handleSelectAll()} /></td>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Merchant'} sortable={true} datafield={'componentMerchant'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Nickname'} sortable={true} datafield={'componentNickname'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Status'} sortable={true} datafield={'utgStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Version'} sortable={true} datafield={'utgVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Agent Version'} sortable={true} datafield={'agentVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Configuration Status'} sortable={true} datafield={'configStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile'} sortable={true} datafield={'componentProfileName'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Version'} sortable={true} datafield={'componentProfileVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Location/Serial'} sortable={true} datafield={'componentLocation'}></TableColumnHeader>
                        <TableColumnHeader title={'Lane'} sortable={true} datafield={'componentLane'}></TableColumnHeader>
                        <TableColumnHeader title={'File ID'} sortable={true} datafield={'componentFileId'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine DNS Name'} sortable={true} datafield={'machineDnsName'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Status'} sortable={true} datafield={'machineStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Known Communication'} sortable={true} datafield={'machineStatusEntityLastKnownCommunication'}></TableColumnHeader>
                        <TableColumnHeader title={'Next Predicted Communication'} sortable={true} datafield={'machineStatusEntityNextPredictedCommunication'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Nickname'} sortable={true} datafield={'machineNickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntityId'}></TableColumnHeader>
                        <TableColumnHeader title={'Time Zone'} sortable={true} datafield={'machineStatusEntityStandardTimeZone'}></TableColumnHeader>
                        <TableColumnHeader title={'Windows Version'} sortable={true} datafield={'machineStatusEntityWindowsVersion'}></TableColumnHeader>
                    </tr>
                </thead>
                <TableRows items={displayDataSignal.value} />
                {
                    displayDataSignal.value.length > 0 && (
                        <tfoot>
                            <tr>
                                <td className="fs-6" colSpan={19}>
                                    Displaying {startDisplayRowNumberSignal.value}-{endDisplayRowNumberSignal.value} of {filteredDataSignal.value.length}
                                </td>
                            </tr>
                        </tfoot>
                    )
                }
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debugSignal.value} />

            <ScheduleTaskModal
                showSignal={showModalTaskSignal}
                handleCancel={handleCancelModalTask}
                selectedUtgsSignal={selectedRowsSignal}
                availableAgentsVersionsSignal={availableAgentsSignal}
                availableUtgVersionsSignal={availableutgListSignal}
                defaultAgentVersion={gaAgentSignal.value}
                defaultUtgVersion={gaUtgSignal.value}
                showDownloadProgressSignal={showDownloadProgressSignal}
                downloadProgressMessageSignal={downloadProgressMessageSignal}
            ></ScheduleTaskModal>

            <ApplyConfigTemplateModal
                show={showModalConfigTemplateSignal.value}
                handleCancel={handleCancelModalConfigTemplate}
                handleConfirm={handleSubmitModalConfigTemplate}
                selectedUtgsSignal={selectedRowsSignal}
                configTemplatesSignal={configTemplatesSignal}
                selectedConfigTemplateIdSignal={selectedConfigTemplateIdSignal}
            ></ApplyConfigTemplateModal>

            <ConfirmationRemoveUtgModal
                show={showRemoveUtgModalSignal.value}
                handleCancel={handleCancelRemoveUtg}
                handleSubmitModalDelete={handleSubmitModalRemoveUtg}
                selectedUtgsSignal={selectedRowsSignal}
            ></ConfirmationRemoveUtgModal>

            <ConfirmationApplyProfileModal
                show={showApplyProfileModalSignal.value}
                handleCancel={handleCancelApplyProfile}
                handleSubmit={handleSubmitModalApplyProfile}
                selectedUtgsSignal={selectedRowsSignal}
                sortedProfiles={sortedProfilesSignal.value}
                selectedProfileSignal={selectedProfileSignal}
            ></ConfirmationApplyProfileModal>

            <UtgListFilteringModal
                show={showUtgFilteringModalSignal.value}
                handleCancel={handleCancelUtgFiltering}
                handleSubmit={handleApplyUtgFilters}
            ></UtgListFilteringModal>

            <UtgsInfoModal
                showSignal={showUtgsInfoModalSignal}
                handleCancel={handleCancelModalUtgsInfo}
                referenceGuid={config.referenceGuid}
            ></UtgsInfoModal>
        </>
    )

}

export default UtgList
